// Duration Chart - Replicating Sentry UI (p50, p75, p95)

import { useMemo, useState } from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend 
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { formatDuration } from '@/types/performance';
import type { DurationSeriesPoint } from '@/types/performance';

interface DurationChartProps {
  data: DurationSeriesPoint[];
  isLoading?: boolean;
}

const PERCENTILE_COLORS = {
  p50: '#57BE8C', // Green
  p75: '#F5B000', // Yellow
  p95: '#E1567C', // Pink
};

export function DurationChart({ data, isLoading }: DurationChartProps) {
  const [showP50, setShowP50] = useState(true);
  const [showP75, setShowP75] = useState(true);
  const [showP95, setShowP95] = useState(true);

  const chartData = useMemo(() => {
    return data.map((point) => ({
      time: new Date(point.timestamp).toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      }),
      p50: point.p50,
      p75: point.p75,
      p95: point.p95,
    }));
  }, [data]);

  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Duration</CardTitle>
        </CardHeader>
        <CardContent className="h-[200px] flex items-center justify-center">
          <div className="animate-pulse bg-muted rounded w-full h-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">Duration</CardTitle>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <Checkbox 
                checked={showP50} 
                onCheckedChange={(checked) => setShowP50(!!checked)}
                className="h-3 w-3"
              />
              <span style={{ color: PERCENTILE_COLORS.p50 }}>p50</span>
            </label>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <Checkbox 
                checked={showP75} 
                onCheckedChange={(checked) => setShowP75(!!checked)}
                className="h-3 w-3"
              />
              <span style={{ color: PERCENTILE_COLORS.p75 }}>p75</span>
            </label>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <Checkbox 
                checked={showP95} 
                onCheckedChange={(checked) => setShowP95(!!checked)}
                className="h-3 w-3"
              />
              <span style={{ color: PERCENTILE_COLORS.p95 }}>p95</span>
            </label>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[180px]">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="time" 
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => formatDuration(value)}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number, name: string) => [
                    formatDuration(value), 
                    name.toUpperCase()
                  ]}
                />
                {showP50 && (
                  <Line
                    type="monotone"
                    dataKey="p50"
                    stroke={PERCENTILE_COLORS.p50}
                    strokeWidth={2}
                    dot={false}
                  />
                )}
                {showP75 && (
                  <Line
                    type="monotone"
                    dataKey="p75"
                    stroke={PERCENTILE_COLORS.p75}
                    strokeWidth={2}
                    dot={false}
                  />
                )}
                {showP95 && (
                  <Line
                    type="monotone"
                    dataKey="p95"
                    stroke={PERCENTILE_COLORS.p95}
                    strokeWidth={2}
                    dot={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              Sem dados de duração
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
